import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Body1, Body2, Button, Dialog } from "@activecollab/components";
import * as PropTypes from "prop-types";
import { useGetInitial } from "~/components/Hooks/useGetInitial";
import FreePlanSurveyForm from "~/components/Subscription/BuyingProcessSteps/FreePlanSurveyForm";
import { activateFreePlan } from "~/store/actions/actions.subscriptionPlans";
import { getIsSuspendedTrial } from "~/store/selectors/selector.accountSettings";
import { showErrorToast, showSuccessToast } from "../Toast";

const ActivateFreePlanDialog = ({
  open,
  onClose,
  freePlan,
  freePlanSurveyAvailable,
  isTrial,
  setBuyingProcessSuccessPlan,
  setShowBuyingProcessSuccess,
  setReload,
  isFromCancellation,
}) => {
  const freePlanUrl = `https://activecollab.com/help/books/freeplan/activation`;
  const navigate = useNavigate();
  const isSuspendedTrial = useSelector(getIsSuspendedTrial);

  const dispatch = useDispatch();
  const getInitial = useGetInitial();
  const [form, setForm] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const isFreeAccountSurveyValid = useCallback((isValid) => {
    setIsFormValid(isValid);
  }, []);

  const getTheFormData = useCallback((surveyResult) => {
    setForm(surveyResult);
  }, []);

  const switchToFreePlan = useCallback(() => {
    onClose();

    let surveyData = null;

    if (isTrial || isSuspendedTrial) {
      surveyData = form;
    }

    dispatch(activateFreePlan(surveyData, isFromCancellation)).then(
      async (result) => {
        if (result.response && result.response.is_ok) {
          if (isTrial || isSuspendedTrial) {
            setBuyingProcessSuccessPlan("You have successfully activated ActiveCollab Free!");
            setReload(true);

            return setShowBuyingProcessSuccess(true);
          }

          showSuccessToast("Plan successfully changed.");
          await getInitial();
          return navigate("/projects");
        } else {
          showErrorToast("Can't activate Free Plan, please contact support!");
        }
      },
      () => {
        showErrorToast("Can't activate Free Plan, please contact support!");
      }
    );
  }, [
    onClose,
    isTrial,
    isSuspendedTrial,
    dispatch,
    isFromCancellation,
    form,
    getInitial,
    navigate,
    setBuyingProcessSuccessPlan,
    setReload,
    setShowBuyingProcessSuccess,
  ]);

  const checkForFreePlan = useMemo(() => {
    if (freePlan?.restriction_reasons.length > 0) {
      return true;
    }
    if (freePlanSurveyAvailable && (isTrial || isSuspendedTrial)) {
      return !isFormValid;
    }

    return false;
  }, [freePlan?.restriction_reasons, isFormValid, freePlanSurveyAvailable, isSuspendedTrial, isTrial]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      enableBackgroundClick
    >
      <Dialog.Title>Activate Free Plan</Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        {freePlan?.restriction_reasons.length > 0 ? (
          <>
            <div className="tw-flex-col tw-items-center">
              <Body2 color="alert">{freePlan?.restriction_reasons[0]}</Body2>
              <Body2>
                <a
                  href={freePlanUrl}
                  className="tw-py-2 tw-w-full"
                  rel="noreferrer"
                  target="_blank"
                >
                  <Button
                    variant="text colored"
                    className="tw-underline"
                    onClick={onClose}
                  >
                    Learn More
                  </Button>
                </a>
              </Body2>
            </div>
            <Dialog.ContentDivider className="tw-my-5 tw--mx-[30px]" />
          </>
        ) : null}
        <Body1 className="tw-mb-4">
          {"By activating the Free Plan, you will lose access to all the features not included in the plan." +
            " You can upgrade to the Pro or Plus plan whenever you like."}
        </Body1>
        {freePlanSurveyAvailable && (isTrial || isSuspendedTrial) && freePlan?.restriction_reasons.length === 0 ? (
          <div>
            <Dialog.ContentDivider />
            <FreePlanSurveyForm
              getSurveyResult={getTheFormData}
              validate={isFreeAccountSurveyValid}
            />
          </div>
        ) : null}
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          variant="primary"
          className="tw-mr-3"
          onClick={switchToFreePlan}
          disabled={checkForFreePlan}
        >
          Activate
        </Button>
        <Button
          variant="secondary"
          data-action="cancel"
          onClick={onClose}
        >
          Cancel
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

ActivateFreePlanDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  freePlan: PropTypes.any,
  freePlanSurveyAvailable: PropTypes.any,
  isTrial: PropTypes.bool,
  setBuyingProcessSuccessPlan: PropTypes.func,
  setShowBuyingProcessSuccess: PropTypes.func,
  setReload: PropTypes.func,
  isFromCancellation: PropTypes.bool,
};

export default ActivateFreePlanDialog;
